// src/api/utils/chunkRouter.ts
import {
  ChatResponseChunk,
  ChatStreamCallbacks,
  isWebSearchQuery,
  isWebFetchUrl,
  isWebSearchResults,
  isWebFetchResult,
  isServerToolUse,
  AutoRedlineOutput,
  isFormRequestPayload,
  FormRequestPayload,
  isPlaybookToolResult,
  normalizePlaybookResult,
  NormalizedPlaybookResult,
  isPlaybookSelectionResult,
  normalizePlaybookSelectionResult,
} from "../../interfaces/chatTypes";
import { isAutoRedlineOutput } from "../../interfaces/autoRedlineTypes";
import { isAddCommentOutput, AddCommentOutput } from "../../interfaces/addCommentTypes";
import {
  isCatylexSearchResult,
  normalizeCatylexSearchResult,
  parseCatylexToolInput,
  CATYLEX_READ_ERROR_DETAIL,
  CATYLEX_SEARCH_ERROR_DETAIL,
} from "../../interfaces/catylexSearchTypes";
import {
  isSharePointSearchResult,
  normalizeSharePointResult,
  parseSharePointToolInput,
} from "../../interfaces/sharepointSearchTypes";
import {
  isOutlookMailResult,
  normalizeOutlookMailResult,
  parseOutlookMailToolInput,
} from "../../interfaces/outlookMailTypes";
import { ChatApiError } from "../chatApi";
import { normalizeWebCitations } from "../../interfaces/webCitationTypes";

// Logger type definition (matches the actual Logger class interface)
interface Logger {
  debug: (message: string, meta?: any) => void;
  info: (message: string, meta?: any) => void;
  warn: (message: string, meta?: any) => void;
  error: (message: string, meta?: any) => void;
}

/**
 * Configuration options for chunk routing behavior
 */
/**
 * Mutable state that persists across chunk router calls within a single streaming session.
 * Create once per session and pass the same reference for every routeChunkToCallback call.
 */
export interface ChunkRouterState {
  /** Name of the currently active tool (set by tool_use_start, cleared by tool_use_result) */
  activeToolName?: string;
  /**
   * Last known SharePoint action ("search" | "read"), set from input_json_delta.
   * Cleared with activeToolName: the state is per-API-instance and only reset at
   * request start, so a value left behind by call #1 would describe call #2.
   */
  lastSharePointAction?: "search" | "read";
  /** Last known Catylex action ("search" | "read"), set from input_json_delta */
  lastCatylexAction?: "search" | "read";
  /**
   * contract_id of the in-flight Catylex read, set from input_json_delta. Only a
   * stream error needs it: an error chunk carries no payload, so this is the sole
   * source for the contract_id memorySessionManager replays in the failed read's
   * tool_use block when it rebuilds history for the next turn.
   */
  lastCatylexContractId?: string;
}

export interface ChunkRouterConfig {
  /**
   * Enable verbose logging for auto-redline tool results
   * (includes requiresSync flags for each edit)
   */
  verboseAutoRedlineLogging?: boolean;

  /**
   * Enable verbose debugging for web search queries
   * (includes detailed property inspection)
   */
  verboseWebSearchLogging?: boolean;

  /**
   * Enable enhanced error handling for text/thinking chunks
   * (converts non-string values to JSON strings with warnings)
   */
  enhancedErrorHandling?: boolean;

  /**
   * Enable fallback to text callback for unknown chunk types
   * (treats unknown types as text content)
   */
  enableFallbackToText?: boolean;

  /**
   * Mutable state object for tracking context across chunks within a session.
   * If not provided, tool context tracking is disabled and input_json_delta
   * will use heuristic matching (may misidentify tool inputs as web search queries).
   */
  state?: ChunkRouterState;
}

/**
 * Routes streaming response chunks to appropriate callbacks
 *
 * This utility provides centralized chunk handling logic for all agent APIs,
 * reducing code duplication while maintaining flexibility through configuration.
 *
 * @param chunk - The streaming response chunk to route
 * @param callbacks - The callback functions to invoke
 * @param log - Logger instance for this routing operation
 * @param config - Optional configuration to customize routing behavior
 */
export function routeChunkToCallback(
  chunk: ChatResponseChunk,
  callbacks: ChatStreamCallbacks,
  log: Logger,
  config: ChunkRouterConfig = {}
): void {
  const chunkType = chunk.type;

  switch (chunkType) {
    // Message lifecycle
    case "message_start":
      if (callbacks.onMessageStart) {
        callbacks.onMessageStart(chunk.ai_trace_id);
      }
      log.debug("Message streaming started");
      break;

    case "message_stop":
      if (callbacks.onMessageStop) {
        callbacks.onMessageStop(chunk.stop_reason);
      }
      if (chunk.stop_reason === "max_tokens") {
        log.warn("Response truncated: stop_reason=max_tokens");
      } else {
        log.debug("Message streaming completed");
      }
      break;

    // Text content streaming
    case "text_start":
      if (callbacks.onTextStart) {
        callbacks.onTextStart();
      }
      log.debug("Text streaming started");
      break;

    case "text":
      // Core text content - this maintains backward compatibility
      if (typeof chunk.text === "string") {
        callbacks.onTextChunk(chunk.text);
      } else if (config.enhancedErrorHandling) {
        // Enhanced error handling: convert objects to JSON strings
        log.warn("Received object in text chunk, converting to string", {
          originalContent: chunk.text,
        });
        callbacks.onTextChunk(JSON.stringify(chunk.text));
      } else {
        // Basic handling: just pass string chunks
        log.warn("Received non-string in text chunk", {
          type: typeof chunk.text,
        });
      }
      break;

    case "text_stop":
      if (callbacks.onTextStop) {
        callbacks.onTextStop();
      }
      log.debug("Text streaming completed");
      break;

    // Thinking/reasoning process
    case "thinking_start":
      if (callbacks.onThinkingStart) {
        callbacks.onThinkingStart();
      }
      log.debug("AI thinking started");
      break;

    case "thinking":
      // Thinking content - maintained for future UI implementation
      if (typeof chunk.text === "string") {
        callbacks.onThinkingChunk(chunk.text);
      } else if (config.enhancedErrorHandling) {
        // Enhanced error handling: convert objects to JSON strings
        log.warn("Received object in thinking chunk, converting to string", {
          originalContent: chunk.text,
        });
        callbacks.onThinkingChunk(JSON.stringify(chunk.text));
      } else {
        // Basic handling: just pass string chunks
        log.warn("Received non-string in thinking chunk", {
          type: typeof chunk.text,
        });
      }
      break;

    case "thinking_stop": {
      let signature: string | null = null;
      if (typeof chunk.text === "object" && chunk.text !== null) {
        const sigRaw = (chunk.text as Record<string, unknown>).signature;
        if (typeof sigRaw === "string") {
          signature = sigRaw;
        }
      }
      if (callbacks.onThinkingStop) {
        callbacks.onThinkingStop(signature);
      }
      log.debug("AI thinking completed", { hasSignature: signature !== null });
      break;
    }

    // Web search functionality
    case "server_tool_use":
      if (typeof chunk.text === "object" && isServerToolUse(chunk.text)) {
        if (callbacks.onWebSearchToolUse) {
          callbacks.onWebSearchToolUse(chunk.text);
        }
        log.debug("Server tool use detected", {
          toolName: chunk.text.name,
        });
      } else {
        log.warn("Invalid server_tool_use content", {
          content: chunk.text,
        });
      }
      break;

    case "input_json_delta":
      // input_json_delta is used for incrementally building JSON payloads for ANY tool
      // The final result arrives via dedicated chunk types (form_request, tool_use_result, etc.)
      // Only process web search/fetch queries here; other data types are intermediate
      if (typeof chunk.text === "object") {
        // Use active tool context (if available) to avoid misidentifying tool inputs as web search
        const activeToolIsWebSearch =
          !config.state?.activeToolName || config.state.activeToolName === "web_search";

        // Check for web search query format: { query: string }
        // Only treat as web search if the active tool is actually web_search (or unknown)
        if (activeToolIsWebSearch && isWebSearchQuery(chunk.text)) {
          if (config.verboseWebSearchLogging) {
            log.debug("DEBUG: input_json_delta event received", {
              textType: typeof chunk.text,
              isWebSearchQuery: true,
            });
          }
          if (callbacks.onWebSearchQuery) {
            callbacks.onWebSearchQuery(chunk.text);
          }
          log.debug("Web search query received", {
            query: chunk.text.query,
          });
        }
        // Check for web fetch URL format: { url: string }
        // Note: Web fetch doesn't need this - URL is in the result
        // Just log and continue (not an error)
        else if (isWebFetchUrl(chunk.text)) {
          log.debug("Web fetch URL in input_json_delta (informational)", {
            url: chunk.text.url,
          });
        }
        // SharePoint search tool input capture
        else if (config.state?.activeToolName === "sharepoint_search") {
          const parsed = parseSharePointToolInput(chunk.text as Record<string, unknown>);
          if (parsed) {
            if (config.state) {
              config.state.lastSharePointAction = parsed.action;
            }
            if (callbacks.onSharePointToolInput) {
              callbacks.onSharePointToolInput(parsed);
            }
          }
        }
        // Catylex search tool input capture
        else if (config.state?.activeToolName === "catylex_search") {
          const parsed = parseCatylexToolInput(chunk.text as Record<string, unknown>);
          if (parsed) {
            if (config.state) {
              config.state.lastCatylexAction = parsed.action;
              config.state.lastCatylexContractId = parsed.contractId;
            }
            if (callbacks.onCatylexToolInput) {
              callbacks.onCatylexToolInput(parsed);
            }
          }
        }
        // Outlook mail tool input capture
        else if (config.state?.activeToolName === "outlook_mail") {
          const parsed = parseOutlookMailToolInput(chunk.text as Record<string, unknown>);
          if (parsed) {
            callbacks.onOutlookMailToolInput?.(parsed);
          }
        }
        // Non-web-search/fetch input_json_delta chunks (form fields, tool inputs, etc.)
        // are intermediate chunks - the complete payloads arrive via form_request, tool_use_result, etc.
        // No warning needed for these - they're expected behavior
      }
      break;

    case "web_search_tool_result":
      if (typeof chunk.text === "object" && isWebSearchResults(chunk.text)) {
        if (callbacks.onWebSearchResults) {
          callbacks.onWebSearchResults(chunk.text);
        }
        log.debug("Web search results received", {
          resultCount: chunk.text.length,
          firstResult: chunk.text[0]?.title,
        });
      } else {
        log.warn("Invalid web_search_tool_result content", {
          content: chunk.text,
        });
      }
      break;

    // Web fetch functionality
    case "web_fetch_tool_result":
      if (typeof chunk.text === "object" && isWebFetchResult(chunk.text)) {
        log.debug("Web fetch result received", {
          status: chunk.text.status,
          url: chunk.text.url,
        });

        if (callbacks.onWebFetchResult) {
          callbacks.onWebFetchResult(chunk.text);
        }
      } else {
        log.warn("Invalid web_fetch_tool_result content", {
          content: chunk.text,
        });
      }
      break;

    // Tool use start notification — backend sends the tool name directly as text
    case "tool_use_start": {
      const toolName = typeof chunk.text === "string" ? chunk.text : undefined;

      if (config.state) {
        config.state.activeToolName = toolName;
      }

      log.debug("Tool use started", { toolName });

      if (callbacks.onToolUseStart) {
        callbacks.onToolUseStart(toolName || "");
      }
      break;
    }

    // Tool use results (playbook, auto-redline, etc.)
    case "tool_use_result":
      // Clear the whole per-call context — the tool execution is complete. The
      // action fields must go with activeToolName: state lives for the entire
      // request, so a "read" left over from call #1 would be read by the error
      // branch on call #2 and describe a failed search as a failed load.
      if (config.state) {
        config.state.activeToolName = undefined;
        config.state.lastSharePointAction = undefined;
        config.state.lastCatylexAction = undefined;
        config.state.lastCatylexContractId = undefined;
      }

      if (typeof chunk.text === "object" && chunk.text !== null) {
        const isPlaybookSelection = isPlaybookSelectionResult(chunk.text);
        const isPlaybook = isPlaybookToolResult(chunk.text);

        // PRIORITY 0: Check for playbook selection card (content.type === "playbook_selection")
        // Must precede the resolved-playbook branch — both share tool_name but the
        // selection card carries a `playbooks` array instead of `playbook` content.
        if (isPlaybookSelection) {
          const normalized = normalizePlaybookSelectionResult(chunk.text);

          if (normalized) {
            log.debug("Playbook selection card detected and normalized", {
              playbookCount: normalized.playbooks.length,
            });

            if (callbacks.onPlaybookSelectionRequest) {
              callbacks.onPlaybookSelectionRequest(normalized);
            }
          } else {
            log.warn("Playbook selection detected but normalization failed", {
              chunkKeys: Object.keys(chunk.text as object),
            });
          }
        }
        // PRIORITY 1: Check for playbook tool result (has tool_name field)
        else if (isPlaybook) {
          const normalized = normalizePlaybookResult(chunk.text);

          if (normalized) {
            log.debug("Playbook tool result detected and normalized", {
              contentLength: normalized.content_length,
              sectionCount: normalized.sections?.length || 0,
            });

            if (callbacks.onPlaybookToolResult) {
              callbacks.onPlaybookToolResult(normalized);
            }
          } else {
            log.warn("Playbook detected but normalization failed", {
              chunkKeys: Object.keys(chunk.text as object),
            });
          }
        }
        // PRIORITY 2: Check for auto-redline tool result
        // After handler update, auto_redline arrives as full object with tool_name
        // (like playbook/catylex). Extract content and preserve tool_use_id/tool_input.
        else if (
          (chunk.text as Record<string, unknown>).tool_name === "auto_redline_tool"
            ? isAutoRedlineOutput((chunk.text as Record<string, unknown>).content)
            : isAutoRedlineOutput(chunk.text)
        ) {
          const raw = chunk.text as Record<string, unknown>;
          const hasWrapper = raw.tool_name === "auto_redline_tool";
          const output = hasWrapper
            ? {
                ...(raw.content as AutoRedlineOutput),
                tool_use_id: raw.tool_use_id as string,
                tool_input: raw.tool_input as Record<string, unknown>,
              }
            : (raw as unknown as AutoRedlineOutput);

          if (callbacks.onAutoRedlineToolResult) {
            callbacks.onAutoRedlineToolResult(output);
          }

          if (config.verboseAutoRedlineLogging) {
            // Verbose logging with requiresSync flags
            log.debug("Auto-redline tool result processed", {
              sectionName: output.sectionName,
              editCount: output.edits.length,
              editsWithRequiresSync: output.edits.filter((e) => e.requiresSync).length,
              requiresSyncFlags: output.edits.map((e) => ({
                editId: e.editId,
                requiresSync: e.requiresSync,
              })),
            });
          } else {
            // Standard logging
            log.debug("Auto-redline tool result processed", {
              sectionName: output.sectionName,
              editCount: output.edits.length,
              editsWithRequiresSync: output.edits.filter((e) => e.requiresSync).length,
            });
          }
        }
        // PRIORITY 2.5: Check for add-comment tool result
        // Always arrives wrapped: { tool_name: "add_comment_tool", tool_use_id,
        // tool_input, content }. Unwrap content and preserve tool_use_id/tool_input.
        else if (
          (chunk.text as Record<string, unknown>).tool_name === "add_comment_tool" &&
          isAddCommentOutput((chunk.text as Record<string, unknown>).content)
        ) {
          const raw = chunk.text as Record<string, unknown>;
          const output: AddCommentOutput = {
            ...(raw.content as AddCommentOutput),
            tool_use_id: raw.tool_use_id as string,
            tool_input: raw.tool_input as Record<string, unknown>,
          };

          if (callbacks.onAddCommentToolResult) {
            callbacks.onAddCommentToolResult(output);
            log.debug("Add-comment tool result processed", {
              sectionName: output.sectionName,
              paragraphId: output.paragraphId,
              hasAnchorPhrase: !!output.anchorPhrase,
            });
          } else {
            // The chunk was recognized but no callback is wired — the processing
            // card will stay stuck on "Adding a comment…" forever. This means a
            // plumbing layer (modelService / agentAwareModelService / ModelContext)
            // dropped onAddCommentToolResult. See AddCommentTool-FE-Implementation.md.
            log.warn(
              "add_comment_tool result recognized but no onAddCommentToolResult callback is registered — " +
                "the comment card will remain stuck on 'processing'. Check the callback plumbing chain.",
              { toolUseId: output.tool_use_id, sectionName: output.sectionName }
            );
          }
        }
        // PRIORITY 3: Check for SharePoint search result
        // No normalization-failure branch: normalizeSharePointResult returns null
        // only when isSharePointSearchResult fails, and that guard just passed —
        // every other shape becomes an error result inside the normalizer.
        else if (isSharePointSearchResult(chunk.text)) {
          const normalized = normalizeSharePointResult(chunk.text);

          if (normalized) {
            log.debug("SharePoint search result detected and normalized", {
              action: normalized.action,
              status: normalized.status,
            });

            if (callbacks.onSharePointSearchResult) {
              callbacks.onSharePointSearchResult(normalized);
            }
          }
        }
        // PRIORITY 4: Check for Catylex search result
        // No normalization-failure branch: normalizeCatylexSearchResult returns
        // null only when isCatylexSearchResult fails, and that guard just passed.
        // An unrecognized status is turned into an error result by the normalizer,
        // so there is no shape left for a fallback to describe.
        else if (isCatylexSearchResult(chunk.text)) {
          const normalized = normalizeCatylexSearchResult(chunk.text);

          if (normalized) {
            log.debug("Catylex tool result detected and normalized", {
              action: normalized.action,
              status: normalized.status,
              searchType: normalized.searchType,
              totalResults: normalized.totalResults,
              sentencesReturned: normalized.sentencesReturned,
            });

            if (callbacks.onCatylexSearchResult) {
              callbacks.onCatylexSearchResult(normalized);
            }
          }
        }
        // PRIORITY 5: Check for Outlook mail result
        else if (isOutlookMailResult(chunk.text)) {
          const normalized = normalizeOutlookMailResult(chunk.text);

          log.debug("Outlook mail result detected and normalized", {
            action: normalized.action,
            status: normalized.status,
          });

          callbacks.onOutlookMailResult?.(normalized);
        } else {
          // Unknown tool_use_result format — no branch claimed it. Log enough to
          // diagnose a BE/FE shape mismatch (tool_name + the content's keys) so a
          // dropped/renamed field is obvious from the console without a debugger.
          const rawChunk = chunk.text as Record<string, unknown>;
          const innerContent = rawChunk.content;
          log.warn("Unknown tool_use_result content — no router branch matched", {
            typeOfContent: typeof chunk.text,
            hasToolName: "tool_name" in rawChunk,
            toolName: rawChunk.tool_name,
            envelopeKeys: Object.keys(rawChunk),
            contentKeys:
              innerContent && typeof innerContent === "object"
                ? Object.keys(innerContent as Record<string, unknown>)
                : typeof innerContent,
          });
        }
      } else {
        log.warn("Invalid tool_use_result content - not an object", {
          typeOfContent: typeof chunk.text,
        });
      }
      break;

    // Workflow form request
    case "form_request":
      if (typeof chunk.text === "object" && isFormRequestPayload(chunk.text)) {
        const payload = chunk.text as FormRequestPayload;

        log.info("📋 Form request received", {
          formType: payload.formType,
          fieldCount: payload.fields.length,
        });

        if (callbacks.onFormRequest) {
          callbacks.onFormRequest(payload);
        }
      } else {
        log.warn("Invalid form_request content", {
          receivedKeys:
            typeof chunk.text === "object" && chunk.text !== null ? Object.keys(chunk.text) : "N/A",
        });
      }
      break;

    // Document citation
    case "document_citation":
      if (typeof chunk.text === "string" && callbacks.onDocumentCitation) {
        try {
          const citation = JSON.parse(chunk.text);
          callbacks.onDocumentCitation(citation);
          log.info("Document citation received", {
            ref: citation.ref,
            documentTitle: citation.document_title,
            citedText: citation.cited_text?.substring(0, 80),
          });
        } catch (parseError) {
          log.warn("Failed to parse document_citation chunk", {
            text: typeof chunk.text === "string" ? chunk.text.substring(0, 100) : "[non-string]",
          });
        }
      }
      break;

    // Web citations rebuilt from Gemini's search grounding
    case "web_citations": {
      const insertions = normalizeWebCitations(chunk.text);
      if (insertions.length > 0 && callbacks.onWebCitations) {
        callbacks.onWebCitations(insertions);
      }
      log.info("Web citations received", { insertions: insertions.length });
      break;
    }

    // Error handling
    case "error": {
      const errorMessage = typeof chunk.text === "string" ? chunk.text : JSON.stringify(chunk.text);

      // If error arrives while sharepoint_search is active, route to SP callback too
      if (
        config.state?.activeToolName === "sharepoint_search" &&
        callbacks.onSharePointSearchResult
      ) {
        log.warn(
          "Error during active sharepoint_search — routing to search result and error callbacks",
          { errorMessage }
        );
        const spAction = config.state?.lastSharePointAction || "search";
        callbacks.onSharePointSearchResult({
          action: spAction,
          status: "error",
          errorMessage:
            errorMessage || (spAction === "read" ? "Failed to fetch document" : "Search failed"),
          errorCode: chunk.error_code || "STREAMING_ERROR",
        });
        if (config.state) {
          config.state.activeToolName = undefined;
          config.state.lastSharePointAction = undefined;
        }
      }

      // If error arrives while catylex_search is active, route to search callback too
      if (config.state?.activeToolName === "catylex_search" && callbacks.onCatylexSearchResult) {
        log.warn(
          "Error during active catylex_search — routing to search result and error callbacks",
          {
            errorMessage,
          }
        );
        const cxAction = config.state?.lastCatylexAction || "search";
        callbacks.onCatylexSearchResult({
          status: "error",
          action: cxAction,
          // Match the normalizer: a read has no query, and "unknown" would print
          // as `Query: "unknown"` in the expanded card.
          originalQuery: cxAction === "read" ? "" : "unknown",
          // The DETAIL copy — this renders in the expanded body, below a header
          // that already says the summary.
          errorMessage:
            errorMessage ||
            (cxAction === "read" ? CATYLEX_READ_ERROR_DETAIL : CATYLEX_SEARCH_ERROR_DETAIL),
          errorCode: chunk.error_code || "STREAMING_ERROR",
          // The error chunk carries no payload; the id captured from the tool
          // input is the only way history can replay which contract was asked
          // for, so the next turn doesn't retry the same failing read blind.
          contractId: cxAction === "read" ? config.state?.lastCatylexContractId : undefined,
        });
        // Clear tool state since the tool execution is done
        if (config.state) {
          config.state.activeToolName = undefined;
          config.state.lastCatylexAction = undefined;
          config.state.lastCatylexContractId = undefined;
        }
      }

      // If error arrives while outlook_mail is active, route to mail result callback too
      if (config.state?.activeToolName === "outlook_mail" && callbacks.onOutlookMailResult) {
        log.warn("Error during active outlook_mail — routing to mail result and error callbacks", {
          errorMessage,
        });
        const errorResult = {
          status: "error" as const,
          action: "preview" as const,
          toolUseId: "",
          subject: "",
          toRecipients: [],
          ccRecipients: [],
          hasAttachment: false,
          attachmentName: null,
          errorMessage:
            typeof chunk.text === "string" ? chunk.text : "An error occurred with the email tool",
          errorCode: "TOOL_ERROR",
        };
        callbacks.onOutlookMailResult(errorResult);
        // Clear tool state since the tool execution is done
        if (config.state) {
          config.state.activeToolName = undefined;
        }
      }

      // Always call onError so general infrastructure (isGenerating, error toasts) resets
      callbacks.onError(
        new ChatApiError(
          errorMessage,
          chunk.error_code || "STREAMING_ERROR",
          0 // Status code not available in chunk
        )
      );
      log.error("Streaming error received", {
        errorMessage,
      });
      break;
    }

    default:
      if (config.enableFallbackToText) {
        // Handle unknown types gracefully by treating as text
        // This ensures forward compatibility if new types are added
        log.warn("Unknown chunk type received, treating as text", {
          chunkType,
          chunkText:
            typeof chunk.text === "string"
              ? chunk.text.substring(0, 100) + (chunk.text.length > 100 ? "..." : "")
              : "[object]",
        });

        const fallbackText =
          typeof chunk.text === "string" ? chunk.text : JSON.stringify(chunk.text);

        if (fallbackText) {
          callbacks.onTextChunk(fallbackText);
        }
      } else {
        // Just log the unhandled type
        log.warn("Unknown chunk type received", {
          chunkType,
          chunkText:
            typeof chunk.text === "string"
              ? chunk.text.substring(0, 100) + (chunk.text.length > 100 ? "..." : "")
              : "[object]",
        });
      }
  }
}
